import { useState } from 'react';
import { MessageSquare, X, Send, Phone, Mail, User, Building, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuickQuoteButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/quotes`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message
      });
      
      toast.success('Quote request submitted! We\'ll contact you soon.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        data-testid="quick-quote-button"
        className="fixed bottom-20 right-6 z-50 group"
      >
        <div className="relative">
          {/* Pulse animation */}
          <span className="absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-30"></span>
          
          {/* Main button */}
          <div className="relative flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-5 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
            <MessageSquare className="w-5 h-5" />
            <span className="font-semibold hidden sm:inline">Quick Quote</span>
          </div>
        </div>
      </button>

      {/* Quote Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden" data-testid="quick-quote-modal">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-5 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-orange-500" />
                Request a Quick Quote
              </DialogTitle>
              <DialogDescription className="text-slate-300 mt-1">
                Fill out the form below and we'll get back to you within 24 hours.
              </DialogDescription>
            </DialogHeader>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="border-slate-200 focus:border-orange-500"
                  data-testid="quote-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@company.com"
                  required
                  className="border-slate-200 focus:border-orange-500"
                  data-testid="quote-email-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  className="border-slate-200 focus:border-orange-500"
                  data-testid="quote-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-700 flex items-center gap-1">
                  <Building className="w-3.5 h-3.5" />
                  Company
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Marine Services Inc."
                  className="border-slate-200 focus:border-orange-500"
                  data-testid="quote-company-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message" className="text-slate-700">
                What are you looking for? *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="I'm interested in a Caterpillar C32 engine for my commercial vessel. Please provide pricing and availability..."
                required
                rows={4}
                className="border-slate-200 focus:border-orange-500 resize-none"
                data-testid="quote-message-input"
              />
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="flex-1 border-slate-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="quote-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </div>
            
            {/* Trust message */}
            <p className="text-xs text-slate-500 text-center pt-2">
              Your information is secure and will never be shared with third parties.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
